import React from "react";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { practicePhaseMeta, practicePhaseOrder } from "../../flashcardUtils";

function PracticeProgressHeader({
  cards,
  selectedDeck,
  practicePhase,
  practicePhaseIndex,
  practiceMeta,
  practiceIndex,
  practiceProgress,
  isPracticeDone,
  onClose,
}) {
  return (
    <Box sx={{ width: "100%", maxWidth: 980, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: { xs: 2, md: 1.25 } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 700, textTransform: "uppercase" }}>
            Practice mode
          </Typography>
          <Typography sx={{ color: "var(--toeic-ink-deep)", fontSize: { xs: 24, md: 30 }, fontWeight: 800, lineHeight: 1.05 }} noWrap>
            {selectedDeck?.name}
          </Typography>
        </Box>
        <Button onClick={onClose} sx={{ borderRadius: "999px" }}>
          Close
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: { xs: 1.5, md: 1 } }}>
        {practicePhaseOrder.map((phase, index) => {
          const meta = practicePhaseMeta[phase];
          const isActive = practicePhase === phase;
          const isPast = isPracticeDone || index < practicePhaseIndex;

          return (
            <Box
              key={phase}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                p: { xs: 1.2, md: 0.95 },
                borderRadius: "16px",
                border: `1px solid ${isActive ? meta.accent : "#d7eaf5"}`,
                bgcolor: isActive ? meta.surface : "#fffefd",
                boxShadow: isActive ? "0 14px 30px -24px rgba(19, 43, 63, 0.55)" : "none",
                transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
                transform: isActive ? "translateY(-1px)" : "translateY(0)",
              }}
            >
              <Box
                sx={{
                  width: { xs: 32, md: 30 },
                  height: { xs: 32, md: 30 },
                  flex: "0 0 auto",
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                  color: isActive || isPast ? "#fffefd" : "var(--toeic-ink-soft)",
                  bgcolor: isActive || isPast ? meta.accent : "#edf7ff",
                  fontWeight: 800,
                }}
              >
                {index + 1}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 800, fontSize: { xs: 14, md: 13.5 } }}>
                  {meta.label}
                </Typography>
                <Typography sx={{ color: "var(--toeic-ink-soft)", fontWeight: 500, fontSize: { xs: 12.5, md: 12 } }} noWrap>
                  {meta.title}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "auto minmax(180px, 1fr) minmax(0, auto)" },
          alignItems: "center",
          gap: { xs: 0.85, sm: 1.25 },
          p: { xs: 1, sm: 1.1 },
          borderRadius: "16px",
          border: "1px solid #d7eaf5",
          bgcolor: "rgba(255, 254, 253, 0.82)",
          boxShadow: "0 14px 30px -28px rgba(19, 43, 63, 0.45)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: practiceMeta.accent,
              flex: "0 0 auto",
            }}
          />
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 800, whiteSpace: "nowrap" }}>
            {isPracticeDone
              ? "Complete"
              : `${practiceIndex + 1} of ${cards.length || 0}`}
          </Typography>
        </Stack>

        <Box sx={{ display: "grid", gap: 0.45 }}>
          <LinearProgress
            variant="determinate"
            value={practiceProgress}
            sx={{
              height: 7,
              borderRadius: 999,
              bgcolor: "#dff2ff",
              "& .MuiLinearProgress-bar": { bgcolor: practiceMeta.accent, borderRadius: 999 },
            }}
          />
          <Typography sx={{ display: { xs: "block", sm: "none" }, color: "var(--toeic-ink-soft)", fontSize: 12.5, fontWeight: 600 }}>
            {isPracticeDone ? "Great run" : practiceMeta.hint}
          </Typography>
        </Box>

        <Typography
          sx={{
            display: { xs: "none", sm: "block" },
            color: "var(--toeic-ink-soft)",
            fontSize: 13,
            fontWeight: 600,
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {isPracticeDone ? "Great run" : practiceMeta.hint}
        </Typography>
      </Box>
    </Box>
  );
}

export default PracticeProgressHeader;
